import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../lib/api';
import { UserType, MenuConfig, MenuItem } from './useUserVariants';

export interface MenuValidationResult {
  valid: boolean;
  errors: string[];
}

export function useMenuConfig(tenantId: string, userType: UserType) {
  return useQuery({
    queryKey: ['menu-config', tenantId, userType],
    queryFn: async () => {
      const response = await api.get<MenuConfig>(`/themes/${tenantId}/menu/${userType}`);
      return response.data;
    },
    enabled: !!tenantId && !!userType,
  });
}

export function useDefaultMenu(userType: UserType) {
  return useQuery({
    queryKey: ['menu', 'defaults', userType],
    queryFn: async () => {
      const response = await api.get<MenuConfig>(`/menu/defaults/${userType}`);
      return response.data;
    },
    enabled: !!userType,
  });
}

export function useSaveMenuConfig() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      tenantId,
      userType,
      config,
    }: {
      tenantId: string;
      userType: UserType;
      config: MenuConfig;
    }) => {
      const response = await api.put<MenuConfig>(
        `/themes/${tenantId}/menu/${userType}`,
        config
      );
      return response.data;
    },
    onSuccess: (_, { tenantId, userType }) => {
      queryClient.invalidateQueries({ queryKey: ['menu-config', tenantId, userType] });
    },
  });
}

export function useValidateMenuConfig() {
  return useMutation({
    mutationFn: async ({
      tenantId,
      userType,
      config,
    }: {
      tenantId: string;
      userType: UserType;
      config: MenuConfig;
    }) => {
      const response = await api.post<MenuValidationResult>(
        `/themes/${tenantId}/menu/${userType}/validate`,
        config
      );
      return response.data;
    },
  });
}

export function useResetMenuConfig() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ tenantId, userType }: { tenantId: string; userType: UserType }) => {
      const response = await api.post<MenuConfig>(
        `/themes/${tenantId}/menu/${userType}/reset`
      );
      return response.data;
    },
    onSuccess: (_, { tenantId, userType }) => {
      queryClient.invalidateQueries({ queryKey: ['menu-config', tenantId, userType] });
    },
  });
}

export function useAddMenuItem() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      tenantId,
      userType,
      item,
    }: {
      tenantId: string;
      userType: UserType;
      item: MenuItem;
    }) => {
      const response = await api.post<MenuConfig>(
        `/themes/${tenantId}/menu/${userType}/items`,
        item
      );
      return response.data;
    },
    onSuccess: (_, { tenantId, userType }) => {
      queryClient.invalidateQueries({ queryKey: ['menu-config', tenantId, userType] });
    },
  });
}

export function useRemoveMenuItem() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      tenantId,
      userType,
      itemId,
    }: {
      tenantId: string;
      userType: UserType;
      itemId: string;
    }) => {
      const response = await api.delete<MenuConfig>(
        `/themes/${tenantId}/menu/${userType}/items/${itemId}`
      );
      return response.data;
    },
    onSuccess: (_, { tenantId, userType }) => {
      queryClient.invalidateQueries({ queryKey: ['menu-config', tenantId, userType] });
    },
  });
}

export function useToggleMenuItemVisibility() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      tenantId,
      userType,
      itemId,
      visible,
    }: {
      tenantId: string;
      userType: UserType;
      itemId: string;
      visible: boolean;
    }) => {
      const response = await api.put<MenuConfig>(
        `/themes/${tenantId}/menu/${userType}/items/${itemId}/visibility`,
        { visible }
      );
      return response.data;
    },
    onSuccess: (_, { tenantId, userType }) => {
      queryClient.invalidateQueries({ queryKey: ['menu-config', tenantId, userType] });
    },
  });
}
